import { API_ENDPOINTS, httpClientInstance } from "@/api"
import { notifications } from "@mantine/notifications"
import { AlertCircle, CheckCircle } from "lucide-react"
import { createElement } from "react"
import { create } from "zustand"

export interface PerformanceRecommendationItem {
  id: number
  instance_id: string
  instance_name: string
  aws_region: string
  current_instance_type: string
  finding: string
  finding_display: string
  recommended_instance_type: string
  estimated_monthly_savings: string
  estimated_monthly_savings_currency: string
  last_synced_at: string
}

export interface RecommendedOption {
  rank: number
  instanceType: string
  migrationEffort: string
  performanceRisk: number
  savingsOpportunity: {
    estimatedMonthlySavings: { value: number; currency: string }
    savingsOpportunityPercentage: number
  }
  platformDifferences: string[]
  projectedUtilizationMetrics: Array<{
    name: string
    value: number
    statistic: string
  }>
  savingsOpportunityAfterDiscounts: {
    estimatedMonthlySavings: { value: number; currency: string }
    savingsOpportunityPercentage: number
  }
}

export interface UtilizationMetric {
  value: number
  statistic: string
}

export interface PerformanceRecommendationDetail extends PerformanceRecommendationItem {
  instance_arn: string
  aws_account_id: string
  finding_reason_codes: string[]
  performance_risk: string
  performance_risk_display: string
  recommended_options: RecommendedOption[]
  utilization_metrics: Record<string, UtilizationMetric>
  look_back_period_days: number
  created_at: string
}

export interface PerformanceRecommendationFilters {
  finding?: string
  instance_id?: string
}

export interface EnrollmentStatus {
  enrolled: boolean
  status: string
  status_reason: string | null
}

interface PerformanceAdvisorStore {
  recommendations: PerformanceRecommendationItem[]
  selectedRecommendation: PerformanceRecommendationDetail | null
  enrollmentStatus: EnrollmentStatus | null
  isLoading: boolean
  isDetailLoading: boolean
  isEnrollmentLoading: boolean
  isSyncing: boolean
  error: string | null

  fetchRecommendations: (params?: PerformanceRecommendationFilters) => Promise<void>
  fetchRecommendationDetail: (id: number) => Promise<void>
  fetchEnrollmentStatus: () => Promise<void>
  syncRecommendations: () => Promise<void>
  clearSelectedRecommendation: () => void
  reset: () => void
}

const initialState = {
  recommendations: [] as PerformanceRecommendationItem[],
  selectedRecommendation: null as PerformanceRecommendationDetail | null,
  enrollmentStatus: null as EnrollmentStatus | null,
  isLoading: false,
  isDetailLoading: false,
  isEnrollmentLoading: false,
  isSyncing: false,
  error: null,
}

export const usePerformanceAdvisorStore = create<PerformanceAdvisorStore>((set) => ({
  ...initialState,

  fetchRecommendations: async (params?) => {
    set({ isLoading: true, error: null })
    try {
      const qs = new URLSearchParams()
      if (params?.finding) qs.set("finding", params.finding)
      if (params?.instance_id) qs.set("instance_id", params.instance_id)
      const query = qs.toString()
      const url = query
        ? `${API_ENDPOINTS.awsConnect.performanceRecommendations.list}?${query}`
        : API_ENDPOINTS.awsConnect.performanceRecommendations.list
      const data = await httpClientInstance.get<PerformanceRecommendationItem[]>(url)
      set({ recommendations: data, isLoading: false })
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || error.message || "Failed to fetch performance recommendations"
      set({ error: errorMessage, isLoading: false })
      notifications.show({
        title: "Error Loading Recommendations",
        message: errorMessage,
        color: "red",
        icon: createElement(AlertCircle, { size: 18 }),
        autoClose: 5000,
        withCloseButton: true,
      })
    }
  },

  fetchRecommendationDetail: async (id: number) => {
    set({ isDetailLoading: true, selectedRecommendation: null })
    try {
      const data = await httpClientInstance.get<PerformanceRecommendationDetail>(
        API_ENDPOINTS.awsConnect.performanceRecommendations.retrieve(id)
      )
      set({ selectedRecommendation: data, isDetailLoading: false })
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || error.message || "Failed to fetch recommendation details"
      set({ isDetailLoading: false })
      notifications.show({
        title: "Error Loading Details",
        message: errorMessage,
        color: "red",
        icon: createElement(AlertCircle, { size: 18 }),
        autoClose: 5000,
        withCloseButton: true,
      })
    }
  },

  fetchEnrollmentStatus: async () => {
    set({ isEnrollmentLoading: true })
    try {
      const data = await httpClientInstance.get<EnrollmentStatus>(
        API_ENDPOINTS.awsConnect.performanceRecommendations.enrollmentStatus
      )
      set({ enrollmentStatus: data, isEnrollmentLoading: false })
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || error.message || "Failed to fetch enrollment status"
      set({ isEnrollmentLoading: false })
      notifications.show({
        title: "Error Loading Enrollment Status",
        message: errorMessage,
        color: "red",
        icon: createElement(AlertCircle, { size: 18 }),
        autoClose: 5000,
        withCloseButton: true,
      })
    }
  },

  syncRecommendations: async () => {
    set({ isSyncing: true, error: null })
    try {
      const data = await httpClientInstance.post<{ synced: number }>(
        API_ENDPOINTS.awsConnect.performanceRecommendations.sync
      )
      set({ isSyncing: false })
      notifications.show({
        title: "Sync Complete",
        message: `${data.synced} recommendation${data.synced !== 1 ? "s" : ""} synced from AWS`,
        color: "teal",
        icon: createElement(CheckCircle, { size: 18 }),
        autoClose: 4000,
        withCloseButton: true,
      })
    } catch (error: any) {
      const errorMessage = error.message || "Failed to sync performance recommendations"
      set({ error: errorMessage, isSyncing: false })
      notifications.show({
        title: "Sync Failed",
        message: errorMessage,
        color: "red",
        icon: createElement(AlertCircle, { size: 18 }),
        autoClose: 5000,
        withCloseButton: true,
      })
    }
  },

  clearSelectedRecommendation: () => set({ selectedRecommendation: null }),

  reset: () => set(initialState),
}))

export const selectRecommendations = (state: PerformanceAdvisorStore) => state.recommendations
export const selectOptimizedInstances = (state: PerformanceAdvisorStore) =>
  state.recommendations.filter((r) => r.finding === "OPTIMIZED")
export const selectInstancesNeedingAttention = (state: PerformanceAdvisorStore) =>
  state.recommendations.filter((r) => r.finding !== "OPTIMIZED")
